import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import GoldConfetti from '../components/GoldConfetti';

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-amber-200/15 bg-white/5 backdrop-blur px-5 py-4 hover:border-amber-200/30 transition-colors">
      <span className="text-[10px] uppercase tracking-[0.2em] text-amber-200/70 shrink-0 w-20">{label}</span>
      <span className="flex-1 truncate text-white text-sm font-mono">{value}</span>
      <button onClick={onCopy} className="text-xs font-semibold uppercase tracking-wider text-amber-200 hover:text-amber-100 px-2 py-1 rounded border border-amber-200/30 hover:border-amber-200/60 transition-colors">
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function CardPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [emailing, setEmailing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [fadingOut, setFadingOut]     = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${id}/zelle`);
      setData(res.data);
      if (['paid','processing','shipped','delivered'].includes(res.data.order.status)) {
        navigate(`/thank-you/${id}`, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load order');
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const downloadReceipt = () => {
    const token = localStorage.getItem('accessToken');
    // Use a temporary token-bearing form? Simpler: open the HTML endpoint in a new tab.
    // The receipt endpoint requires auth, so we open with the token in a URL hash? No —
    // easiest is to fetch the HTML and stuff it into a Blob URL.
    (async () => {
      try {
        const res = await fetch(`/api/orders/${id}/receipt.html`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load receipt');
        const html = await res.text();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err) {
        toast.error('Could not open receipt');
      }
    })();
  };

  const emailReceipt = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/orders/${id}/email-receipt`);
      toast.success(`Sent to ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Email failed');
    } finally {
      setEmailing(false);
    }
  };

  const completeOrder = async () => {
    setCompleting(true);
    try {
      await api.post(`/orders/${id}/zelle/confirm-intent`);
      // Premium celebration → gentle fade → home to dashboard
      setCelebrating(true);
      setTimeout(() => setFadingOut(true), 2600);
      setTimeout(() => navigate('/dashboard'), 4200);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not complete order');
      setCompleting(false);
    }
  };

  const cancelOrder = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${id}/zelle/cancel`);
      toast.success('Order cancelled');
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  if (error && !data) {
    return (
      <Shell>
        <div className="text-center py-24">
          <h1 className="text-3xl font-light text-amber-100 mb-3">Unable to load order</h1>
          <p className="text-white/60 mb-8">{error}</p>
          <Link to="/account" className="inline-block px-8 py-3 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 transition-colors">Go to my account</Link>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32 text-white/40">
          <div className="animate-pulse text-sm tracking-[0.3em] uppercase">Preparing your invoice…</div>
        </div>
      </Shell>
    );
  }

  const { order, zelle } = data;
  // Show Zelle details whenever an email or phone is configured — even if the
  // admin hasn't flipped the "enabled" master switch — so customers waiting on
  // a settlement always have what they need.
  const zelleReady = !!(zelle && (zelle.email || zelle.phone));

  return (
    <Shell fadingOut={fadingOut}>
      <GoldConfetti active={celebrating} />

      {celebrating && (
        <div className="pointer-events-none fixed inset-0 z-[90] flex items-center justify-center">
          <div className="text-center px-6 animate-[fadeInUp_500ms_ease-out_forwards]">
            <div className="text-7xl mb-4 drop-shadow-[0_0_30px_rgba(245,194,66,0.7)]">✦</div>
            <h2 className="font-light text-4xl md:text-5xl text-amber-100 mb-2 tracking-tight">
              Order received
            </h2>
            <p className="text-amber-200/80 text-sm tracking-[0.3em] uppercase">
              Thank you — taking you back to your dashboard
            </p>
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center justify-center gap-3 text-[10px] tracking-[0.4em] uppercase text-white/40 mb-10">
        <span>Cart</span><span className="text-amber-300/50">◆</span>
        <span>Checkout</span><span className="text-amber-300/50">◆</span>
        <span className="text-amber-200">Settlement</span>
      </div>

      {/* Hero */}
      <header className="text-center mb-12 max-w-2xl mx-auto">
        <div className="inline-block px-4 py-1.5 rounded-full border border-amber-300/30 text-amber-200/90 text-[10px] uppercase tracking-[0.3em] mb-5">
          Order #{order.id.slice(0, 8).toUpperCase()}
        </div>
        <h1 className="font-light text-white text-4xl md:text-5xl tracking-tight mb-3">
          A quiet word before we begin
        </h1>
        <p className="text-white/60 text-sm leading-relaxed">
          Our card processor is being onboarded for our peptide research category and will be live shortly.
          To honour your order today, we settle privately via <span className="text-amber-200">Zelle</span> — a direct
          bank-to-bank transfer, no third-party fees, fully reconciled by our concierge desk.
        </p>
        <p className="mt-4 text-white/50 text-xs leading-relaxed italic">
          Please note: our <span className="text-amber-200/90 not-italic">subscription options</span> do offer
          a card payment method, processed securely at the time of enrolment.
        </p>
        <div className="mt-6 inline-flex items-center gap-3 text-amber-100">
          <span className="text-xs uppercase tracking-[0.3em] text-white/40">Amount due</span>
          <span className="text-2xl font-light">${(order.totalInCents / 100).toFixed(2)}</span>
          <span className="text-xs uppercase tracking-wider text-white/40">USD</span>
        </div>
      </header>

      {/* Zelle panel */}
      <section className="max-w-3xl mx-auto rounded-3xl border border-amber-300/20 bg-gradient-to-br from-white/[0.06] to-transparent backdrop-blur p-8 mb-8">
        {zelleReady ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl text-amber-200">⌭</span>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-amber-200/70">Send via Zelle</div>
                <div className="text-white text-xl font-light">
                  {zelle.recipientName || 'Aloha Amino'}
                </div>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {zelle.email && <CopyRow label="Email" value={zelle.email} />}
              {zelle.phone && <CopyRow label="Phone" value={zelle.phone} />}
              <CopyRow label="Memo" value={zelle.memo} />
              <CopyRow
                label="Amount"
                value={`${(order.totalInCents / 100).toFixed(2)} USD`}
              />
            </div>
            {zelle.instructions && (
              <p className="text-sm text-white/60 leading-relaxed border-t border-white/10 pt-5">
                {zelle.instructions}
              </p>
            )}
          </>
        ) : (
          <div className="text-center text-amber-100/80">
            <p className="mb-2 text-lg">Our payments concierge will reach out shortly.</p>
            <p className="text-sm text-white/50">
              Zelle details are being finalised. Click "Email me instructions" below and a member of our team
              will follow up within the hour with full payment details.
            </p>
          </div>
        )}
      </section>

      {/* Receipt / email row */}
      <section className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        <button
          type="button"
          onClick={downloadReceipt}
          className="group rounded-2xl border border-white/10 hover:border-amber-200/40 bg-white/[0.04] backdrop-blur p-5 text-left transition-all"
        >
          <div className="text-amber-200 text-xl mb-2">⤓</div>
          <div className="text-white font-medium">Download receipt</div>
          <div className="text-xs text-white/50 mt-1">Opens a print-ready receipt — Save as PDF.</div>
        </button>
        <button
          type="button"
          onClick={emailReceipt}
          disabled={emailing}
          className="group rounded-2xl border border-white/10 hover:border-amber-200/40 bg-white/[0.04] backdrop-blur p-5 text-left transition-all disabled:opacity-50"
        >
          <div className="text-amber-200 text-xl mb-2">✉</div>
          <div className="text-white font-medium">{emailing ? 'Sending…' : 'Email me instructions'}</div>
          <div className="text-xs text-white/50 mt-1">We'll send the receipt + payment steps to your inbox.</div>
        </button>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto text-center">
        <button
          type="button"
          onClick={completeOrder}
          disabled={completing}
          className="group relative inline-flex items-center gap-4 px-12 py-5 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black font-semibold tracking-[0.2em] uppercase text-sm hover:shadow-2xl hover:shadow-amber-500/30 transition-all disabled:opacity-60"
        >
          <span>{completing ? 'Completing…' : 'Complete order'}</span>
          <span className="group-hover:translate-x-1 transition-transform">→</span>
        </button>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-4">
          By completing the order you confirm you have sent (or will send) the Zelle transfer above.
        </p>
        <button
          type="button"
          onClick={cancelOrder}
          disabled={cancelling}
          className="mt-8 text-xs uppercase tracking-[0.3em] text-white/40 hover:text-red-300 transition-colors disabled:opacity-50"
        >
          {cancelling ? 'Cancelling…' : 'Cancel order'}
        </button>
      </section>

      {/* Summary */}
      <section className="max-w-3xl mx-auto mt-14 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-8">
        <h3 className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-5">Order summary</h3>
        <div className="flex items-center justify-between py-3 border-b border-white/5 text-sm">
          <span className="text-white/60">Order ID</span>
          <span className="text-white font-mono">{order.id.slice(0, 8).toUpperCase()}</span>
        </div>
        <div className="flex items-center justify-between py-3 border-b border-white/5 text-sm">
          <span className="text-white/60">Status</span>
          <span className="text-amber-200 uppercase tracking-widest text-xs">{order.status}</span>
        </div>
        <div className="flex items-center justify-between pt-4">
          <span className="text-amber-200 uppercase tracking-[0.2em] text-xs">Total</span>
          <span className="text-2xl font-light text-amber-100">{fmt(order.totalInCents)}</span>
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children, fadingOut = false }) {
  return (
    <div
      className={'relative min-h-screen text-white overflow-hidden transition-all duration-[1400ms] ' + (fadingOut ? 'opacity-0 scale-[0.98] blur-sm' : 'opacity-100')}
      style={{ background: 'radial-gradient(ellipse at top, #1a1410 0%, #0a0805 55%, #050302 100%)' }}
    >
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[820px] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-amber-700/10 blur-[120px]" />
      <div className="relative border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-amber-200 hover:text-amber-100 transition-colors">
            <span className="text-2xl">✦</span>
            <span className="font-light tracking-[0.3em] uppercase text-sm">Aloha Amino</span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">Concierge settlement</span>
        </div>
      </div>
      <main id="main-content" tabIndex={-1} className="relative max-w-6xl mx-auto px-6 py-14">{children}</main>
      <footer className="relative border-t border-white/5 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center text-[10px] uppercase tracking-[0.4em] text-white/30">
          Discreet · Reconciled by hand · Aloha Amino
        </div>
      </footer>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
